import { useReactFlow, NodeProps } from '@xyflow/react'
import { Filter, Settings, Calendar } from 'lucide-react'
import React, { memo, useState, useEffect, useMemo } from 'react'
import { Button } from '@/components/ui/button'
import useDataSettings from '@/hooks/useDataSettings'
import NodeBaseContainer from '../base/base-container/NodeBaseContainer'
import NodeConfigurationDialog from '../../dialogs/configuration-dialog/NodeConfigurationDialog'
import NodeDataSettingsDialog from '../../dialogs/data-settings-dialog/NodeDataSettingsDialog'
import SchedulingDialog from '../../dialogs/SchedulingDialog'

interface NodeWebExtractData {
  url?: string
  mode?: 'summary' | 'normal'
  includeImages?: boolean
  showImages?: boolean
  extractedContent?: string
  images?: string[]
  lastRefreshed?: string
  automationEnabled?: boolean
  schedule?: any
  statusMessage?: string
  configuration?: any
  isLocked?: boolean
  isMinimized?: boolean
  assignedEmails?: string
  dataSettings?: any
  adultContent?: boolean
  depthLevel?: number
  minimizeLevel?: 'normal' | 'medium' | 'minimum'
  minifiedInfo?: Record<string, any>
  extractionType?: string
  label?: string
  documentRequest?: {
    'sysagent-cmd': string
    processing?: boolean
    timestamp?: number
    [key: string]: any
  }
}

const NodeWebExtract: React.FC<NodeProps & { data: NodeWebExtractData }> = (
  props
) => {
  const { id, data, selected } = props
  const { updateNodeData } = useReactFlow()
  const [url, setUrl] = useState(data.url || '')
  const [mode, setMode] = useState<'summary' | 'normal'>(data.mode || 'normal')
  const [includeImages, setIncludeImages] = useState(!!data.includeImages)
  const [showImages, setShowImages] = useState(data.showImages !== false) // Default to true
  const [adultContent, setAdultContent] = useState(!!data.adultContent)
  const [depthLevel, setDepthLevel] = useState(
    typeof data.depthLevel === 'number' ? data.depthLevel : 2
  )
  const [extractedContent, setExtractedContent] = useState(
    data.extractedContent || ''
  )
  const [images, setImages] = useState<string[]>(data.images || [])
  const [lastRefreshed, setLastRefreshed] = useState(data.lastRefreshed || '')
  const [automationEnabled, setAutomationEnabled] = useState(
    !!data.automationEnabled
  )
  const [schedule, setSchedule] = useState<any>(data.schedule || null)
  const [statusMessage, setStatusMessage] = useState(data.statusMessage || '')
  const [isConfigDialogVisible, setIsConfigDialogVisible] = useState(false)
  const [isSchedulingDialogVisible, setIsSchedulingDialogVisible] =
    useState(false)
  const [isLocked, setIsLocked] = useState(!!data.isLocked)
  const [isMinimized, setIsMinimized] = useState(!!data.isMinimized)
  const [assignedEmails, setAssignedEmails] = useState<string>(
    data.assignedEmails || ''
  )
  const [isDataSettingsDialogVisible, setIsDataSettingsDialogVisible] =
    useState(false)
  const [minimizeLevel, setMinimizeLevel] = useState<
    'normal' | 'medium' | 'minimum'
  >('normal')

  // --- useDataSettings integration ---
  // Prepare exposedData for extractedContent
  const prepareExposedDataFields = () => ({
    extractedContent: {
      value: extractedContent,
      enabled: true,
      label: 'Extracted Content',
      description: 'The main content extracted from the web page(s).',
      permissions: { readable: true, writable: false },
    },
  })

  // Helper to sync node data
  const syncNodeData = (newData: Partial<NodeWebExtractData>) => {
    updateNodeData(id, { ...data, ...newData })
  }

  // useDataSettings hook
  const {
    dataSettings,
    setDataSettings,
    handleDataSettingsChange,
    handleRemoveInputLink,
    handleSyncInputLink,
    handleOpenDataSettingsDialog,
    generateLinkedKnowledgeText,
    fetchAvailableDocumentNodes,
    // prepareExposedDataFields: _unused,
  } = useDataSettings(
    id,
    data as any,
    syncNodeData,
    false,
    '',
    new Map(),
    '',
    setIsDataSettingsDialogVisible
  )

  // Keep exposedData in sync with extractedContent
  useEffect(() => {
    const newExposed = prepareExposedDataFields()
    setDataSettings((prev) => ({
      ...prev,
      exposedData: newExposed,
    }))
    // Also sync to node data so other nodes see it
    syncNodeData({
      dataSettings: { ...dataSettings, exposedData: newExposed },
    } as Partial<NodeWebExtractData>)
  }, [extractedContent])

  // Minimal: ensure exposedData is always present and up-to-date for DataSettingsDialog
  useEffect(() => {
    const exposedData = {
      extractedContent: {
        value: data.extractedContent || '',
        enabled: true,
        label: 'Extracted Content',
        description: 'The main content extracted from the web page(s).',
        permissions: { readable: true, writable: false },
      },
    }
    updateNodeData(id, {
      ...data,
      dataSettings: {
        ...(data.dataSettings || {}),
        exposedData,
      },
    })
  }, [data.extractedContent])

  const isValidUrl = (url: string) => {
    try {
      // Accept only http(s) URLs
      const u = new URL(url)
      return u.protocol === 'http:' || u.protocol === 'https:'
    } catch {
      return false
    }
  }
  const handleExtract = () => {
    setStatusMessage('Extracting...')
    syncNodeData({
      url,
      mode,
      includeImages,
      adultContent,
      depthLevel: adultContent ? depthLevel : undefined,
      automationEnabled,
      schedule,
      statusMessage: 'Extracting...',
      extractedContent,
      showImages, // Persist the showImages preference
    })
    // Use AICommandUtils format with proper status
    syncNodeData({
      documentRequest: {
        'sysagent-cmd': 'WebExtract-Process-Started',
        processing: false,
        timestamp: Date.now(),
      },
    })
  }

  // Use isProcessing flag from documentRequest, like SmartNotesNode
  const isProcessing = !!data.documentRequest?.processing

  // Sync extractedContent, images, lastRefreshed from backend updates
  useEffect(() => {
    setExtractedContent(data.extractedContent || '')
    const imgs: string[] = []
    if (Array.isArray(data.images)) {
      const arr = data.images as unknown[]
      for (let i = 0; i < arr.length; i++) {
        const img = arr[i]
        if (typeof img === 'string') imgs.push(img)
      }
    }
    setImages(imgs)
    setLastRefreshed(data.lastRefreshed || '')
  }, [data.extractedContent, data.images, data.lastRefreshed])

  // Optionally, also update statusMessage if you want to reflect backend changes
  useEffect(() => {
    setStatusMessage(data.statusMessage || '')
  }, [data.statusMessage])

  // Handler for exposed data field toggle
  const handleExposedFieldToggle = (fieldKey: string, checked: boolean) => {
    setDataSettings((prev) => ({
      ...prev,
      exposedData: {
        ...prev.exposedData,
        [fieldKey]: {
          ...prev.exposedData[fieldKey],
          enabled: checked,
        },
      },
    }))
  }

  // Handler for exposed data field permission toggle
  const handleExposedFieldPermissionToggle = (
    fieldKey: string,
    permission: 'readable' | 'writable',
    checked: boolean
  ) => {
    setDataSettings((prev) => ({
      ...prev,
      exposedData: {
        ...prev.exposedData,
        [fieldKey]: {
          ...prev.exposedData[fieldKey],
          permissions: {
            ...prev.exposedData[fieldKey].permissions,
            [permission]: checked,
          },
        },
      },
    }))
  }

  // Always include exposedData for extractedContent in the settings prop
  const dataSettingsWithExposed = {
    ...(data.dataSettings || {}),
    exposedData: {
      extractedContent: {
        value: data.extractedContent || '',
        enabled:
          data.dataSettings?.exposedData?.extractedContent?.enabled ?? true,
        label: 'Extracted Content',
        description: 'The main content extracted from the web page(s).',
        permissions: {
          readable:
            data.dataSettings?.exposedData?.extractedContent?.permissions
              ?.readable ?? true,
          writable:
            data.dataSettings?.exposedData?.extractedContent?.permissions
              ?.writable ?? false,
        },
      },
    },
  }

  // Keep url in sync with data.url (update local state if node's data.url changes externally)
  useEffect(() => {
    if (data.url !== undefined && data.url !== url) {
      setUrl(data.url)
    }
  }, [data.url])

  // Keep mode in sync with data.mode
  useEffect(() => {
    if (data.mode !== undefined && data.mode !== mode) {
      setMode(data.mode)
    }
  }, [data.mode])

  // Keep includeImages in sync with data.includeImages
  useEffect(() => {
    if (
      typeof data.includeImages === 'boolean' &&
      data.includeImages !== includeImages
    ) {
      setIncludeImages(data.includeImages)
    }
  }, [data.includeImages])

  // Keep showImages in sync with data.showImages
  useEffect(() => {
    if (
      typeof data.showImages === 'boolean' &&
      data.showImages !== showImages
    ) {
      setShowImages(data.showImages)
    }
  }, [data.showImages])

  // Keep adultContent in sync with data.adultContent
  useEffect(() => {
    if (
      typeof data.adultContent === 'boolean' &&
      data.adultContent !== adultContent
    ) {
      setAdultContent(data.adultContent)
    }
  }, [data.adultContent])

  // Keep automationEnabled in sync with data.automationEnabled
  useEffect(() => {
    if (
      typeof data.automationEnabled === 'boolean' &&
      data.automationEnabled !== automationEnabled
    ) {
      setAutomationEnabled(data.automationEnabled)
    }
  }, [data.automationEnabled])

  // Keep schedule in sync with data.schedule
  useEffect(() => {
    if (data.schedule !== undefined && data.schedule !== schedule) {
      setSchedule(data.schedule)
    }
  }, [data.schedule])

  // Keep depthLevel in sync with data.depthLevel
  useEffect(() => {
    if (typeof data.depthLevel === 'number' && data.depthLevel !== depthLevel) {
      setDepthLevel(data.depthLevel)
    }
  }, [data.depthLevel])

  // Initialize minimize level from data
  useEffect(() => {
    if (data.minimizeLevel === undefined) {
      updateNodeData(id, { ...data, minimizeLevel: 'normal' })
    } else {
      setMinimizeLevel(data.minimizeLevel)
      setIsMinimized(data.minimizeLevel !== 'normal')
    }
  }, [data.minimizeLevel, id, data, updateNodeData])
  // Keep minifiedInfo in sync with source data
  useEffect(() => {
    const minifiedInfo: Record<string, any> = {}

    if (data.url?.trim()) {
      minifiedInfo.url =
        data.url.substring(0, 50) + (data.url.length > 50 ? '...' : '')
    }

    if (data.extractedContent) {
      const wordCount = data.extractedContent.trim().split(/\s+/).length
      minifiedInfo.extracted = `${wordCount} words`
    }

    if (data.images && data.images.length > 0) {
      minifiedInfo.images = `${data.images.length} images`
    }

    if (data.extractionType) {
      minifiedInfo.type = data.extractionType
    }

    // Only update if there are actual changes
    const currentMinified = data.minifiedInfo || {}
    const hasChanges =
      JSON.stringify(currentMinified) !== JSON.stringify(minifiedInfo)

    if (hasChanges) {
      updateNodeData(id, { ...data, minifiedInfo })
    }  }, [
    data.url,
    data.extractedContent,
    data.extractionType,
    data.images,
    data.minifiedInfo,
    id,
    data,
    updateNodeData,
  ])

  const handleMinimizeLevelChange = (
    level: 'normal' | 'medium' | 'minimum'
  ) => {
    setMinimizeLevel(level)
    setIsMinimized(level !== 'normal')
    updateNodeData(id, {
      ...data,
      minimizeLevel: level,
      isMinimized: level !== 'normal',
    })
  }

  // Compute status for NodeBaseContainer
  const nodeStatus = useMemo(() => {
    // Add your logic here based on your node's AI/processing state
    let status: 'success' | 'loading' | 'error' | 'initial' = 'initial'
    if (data?.statusMessage?.toLowerCase().includes('loading'))
      status = 'loading'
    else if (data?.statusMessage?.toLowerCase().includes('error'))
      status = 'error'
    else if (data?.statusMessage?.toLowerCase().includes('complete'))
      status = 'success'
    else if (data?.statusMessage?.toLowerCase().includes('successful'))
      status = 'success'
    return status
  }, [data?.statusMessage])

  return (
    <NodeBaseContainer
      id={id}
      data={data}
      resizable={true}
      selected={selected} // ✅ CRITICAL: Make sure selected prop is passed!
      nodeTitle={data.label || 'Web Extract'}
      isLocked={isLocked}
      isMinimized={isMinimized}
      assignedEmails={
        assignedEmails ? assignedEmails.split(/\s+/).filter(Boolean) : []
      }
      onAssignUsersClick={() => {
        const emailsStr = window.prompt(
          'Enter emails separated by space',
          assignedEmails || ''
        )
        if (emailsStr !== null) {
          setAssignedEmails(emailsStr)
          syncNodeData({ assignedEmails: emailsStr })
        }
      }}
      customButtons={[
        {
          id: 'config',
          onClick: () => setIsConfigDialogVisible(true),
          icon: <Settings size={16} />,
          tooltip: 'Configuration',
        },
        {
          id: 'schedule',
          onClick: () => setIsSchedulingDialogVisible(true),
          icon: <Calendar size={16} />,
          tooltip: 'Scheduling',
        },
        {
          id: 'data-settings',
          onClick: handleOpenDataSettingsDialog,
          icon: <Filter size={16} />,
          tooltip: 'Data connection settings',
        },
      ]}
      isProcessing={isProcessing}
      status={nodeStatus}
      minimizeLevel={data.minimizeLevel || 'normal'}
      onMinimizeLevelChange={handleMinimizeLevelChange}
    >
      {(data.minimizeLevel || 'normal') === 'normal' && (
        <div className="flex flex-col h-full">
          <div>
            <label className="block text-xs font-semibold mb-1">
              URL to extract
            </label>
            <input
              type="text"
              className="w-full border rounded p-2 nodrag nowheel"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              onBlur={(e) => syncNodeData({ url: e.target.value })}
              disabled={isLocked}
              placeholder="https://example.com/article"
            />
          </div>
          <div className="flex gap-3 items-center">
            <label className="text-xs font-semibold">Mode:</label>
            <select
              className="border rounded p-1"
              value={mode}
              onChange={(e) => {
                setMode(e.target.value as 'summary' | 'normal')
                syncNodeData({ mode: e.target.value as 'summary' | 'normal' })
              }}
              disabled={isLocked}
            >
              <option value="summary">Summary</option>
              <option value="normal">Normal</option>
            </select>
            <label className="flex items-center gap-1 ml-4">
              <input
                type="checkbox"
                checked={includeImages}
                onChange={(e) => {
                  setIncludeImages(e.target.checked)
                  syncNodeData({ includeImages: e.target.checked })
                }}
                disabled={isLocked}
                className="nodrag nowheel"
              />
              <span className="text-xs">Include images</span>
            </label>
            <label className="flex items-center gap-1 ml-4">
              <input
                type="checkbox"
                checked={adultContent}
                onChange={(e) => {
                  setAdultContent(e.target.checked)
                  syncNodeData({ adultContent: e.target.checked })
                }}
                disabled={isLocked}
                className="nodrag nowheel"
              />
              <span className="text-xs">Adult material</span>
            </label>
            {adultContent && (
              <div className="flex items-center gap-1 ml-2">
                <label className="text-xs">Depth level:</label>
                <input
                  type="number"
                  min={1}
                  max={10}
                  value={depthLevel}
                  onChange={(e) => {
                    const val = Number(e.target.value)
                    setDepthLevel(val)
                    syncNodeData({ depthLevel: val })
                  }}
                  disabled={isLocked}
                  className="w-14 border rounded p-1 nodrag nowheel"
                />
              </div>
            )}
          </div>
          <div className="flex gap-3 items-center">
            <label className="flex items-center gap-1">
              <input
                type="checkbox"
                checked={automationEnabled}
                onChange={(e) => {
                  setAutomationEnabled(e.target.checked)
                  syncNodeData({ automationEnabled: e.target.checked })
                }}
                disabled={isLocked}
              />
              <span className="text-xs">Enable automation</span>
            </label>
            <Button
              size="sm"
              variant="outline"
              onClick={() => setIsSchedulingDialogVisible(true)}
              disabled={isLocked || !automationEnabled}
            >
              Scheduling...
            </Button>
            {schedule && (
              <span className="text-xs text-gray-500 ml-2">
                {schedule.summary || 'Custom schedule set'}
              </span>
            )}
          </div>
          <div className="flex-1 flex flex-col">
            <label className="block text-xs font-semibold mb-1">
              Extracted Information
            </label>
            <textarea
              className="w-full h-full border rounded p-2 min-h-[120px] flex-1 nodrag nowheel"
              style={{ flex: 1, height: '100%' }}
              value={extractedContent}
              onChange={(e) => setExtractedContent(e.target.value)}
              onBlur={(e) => syncNodeData({ extractedContent: e.target.value })}
              disabled={isLocked}
              placeholder="Extracted content will appear here..."
            />
          </div>          {images && images.length > 0 && (
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="block text-xs font-semibold">
                  Extracted Images ({images.length})
                </label>
                <label className="flex items-center gap-1">
                  <input
                    type="checkbox"
                    checked={showImages}
                    onChange={(e) => {
                      setShowImages(e.target.checked)
                      syncNodeData({ showImages: e.target.checked })
                    }}
                    disabled={isLocked}
                    className="nodrag nowheel"
                  />
                  <span className="text-xs">Show images</span>
                </label>
              </div>
              {showImages && (
                <div className="flex flex-wrap gap-2">
                  {images.map((img, idx) => (
                    <a
                      key={img + idx}
                      href={img}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <img
                        src={img}
                        alt={`Extracted ${idx}`}
                        className="w-24 h-24 object-cover border rounded"
                      />
                    </a>
                  ))}
                </div>
              )}
            </div>
          )}
          <div className="flex items-center gap-3 text-xs text-gray-500">
            <span>
              Last refreshed: {lastRefreshed ? lastRefreshed : 'Never'}
            </span>
          </div>
          {/* Move Extract button and status message to the bottom */}
          <div className="mt-auto flex flex-col gap-2">
            <Button
              className="w-full py-2 px-4 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
              onClick={handleExtract}
              disabled={isLocked || !isValidUrl(url) || isProcessing}
            >
              Extract
            </Button>
            {!isValidUrl(url) && (
              <div className="text-xs text-red-500 text-center">
                Please enter a valid URL (starting with http:// or https://)
              </div>
            )}
            {data.statusMessage && (
              <div className="text-sm text-gray-600 text-center">
                {data.statusMessage}
              </div>
            )}
          </div>
        </div>
      )}
      {isConfigDialogVisible && (
        <NodeConfigurationDialog
          visible={isConfigDialogVisible}
          configuration={data.configuration || {}}
          onSave={(config) => {
            syncNodeData({ configuration: config })
            setIsConfigDialogVisible(false)
          }}
          onHide={() => setIsConfigDialogVisible(false)}
        />
      )}
      {isSchedulingDialogVisible && (
        <SchedulingDialog
          visible={isSchedulingDialogVisible}
          schedule={schedule}
          onSave={(sched) => {
            setSchedule(sched)
            syncNodeData({ schedule: sched })
            setIsSchedulingDialogVisible(false)
          }}
          onHide={() => setIsSchedulingDialogVisible(false)}
        />
      )}
      {isDataSettingsDialogVisible && (
        <NodeDataSettingsDialog
          visible={isDataSettingsDialogVisible}
          onHide={() => setIsDataSettingsDialogVisible(false)}
          settings={dataSettingsWithExposed}
          nodeId={id}
          onSettingsChange={handleDataSettingsChange}
          onRemoveInputLink={handleRemoveInputLink}
          onSyncInputLink={handleSyncInputLink}
          forceShowAvailableNodes={false}
          debug={false}
        />
      )}
    </NodeBaseContainer>
  )
}

export default memo(NodeWebExtract)
